import { useCallback, useEffect, useState, useMemo } from "react";
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { Link } from "react-router-dom";
import Layout from "../components/Layout";
import { http, formatApiError } from "../lib/api";
import { toast } from "sonner";
import {
  Sun, Coffee, Warning, Clock, Buildings, User, CurrencyInr, Wallet,
  UmbrellaSimple, CheckCircle, XCircle, EnvelopeSimple, Receipt, Flag,
} from "@phosphor-icons/react";

function money(n) {
  if (!n && n !== 0) return "—";
  return new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 })
    .format(Number(n) || 0);
}

function greet() {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  return "Good evening";
}

export default function GlancePage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data } = await http.get("/glance");
      setData(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); const t = setInterval(load, 60_000); return () => clearInterval(t); }, [load]);

  return (
    <Layout
      title="Studio at a Glance"
      subtitle={`${greet()} — everything you need for the day, in one glance.`}
    >
      {loading && <div className="label-overline text-zinc-500">Loading…</div>}

      {data && (
        <div className="space-y-5" data-testid="glance-page">
          {data.is_holiday && (
            <div className="border-2 border-purple-500 bg-purple-50 p-3 text-purple-900 flex items-center gap-2">
              <Sun size={16} weight="bold" /> <span className="label-overline">Today is a studio holiday.</span>
            </div>
          )}

          {/* KPI strip */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-0 border border-zinc-300 bg-white" data-testid="glance-kpis">
            <Kpi label="On Duty" value={data.attendance.present_full + data.attendance.present_half}
                 total={data.attendance.total_staff - data.attendance.on_leave}
                 accent="text-emerald-700" icon={Coffee} testId="kpi-on-duty" />
            <Kpi label="Overdue Tasks" value={data.tasks.overdue_count}
                 accent={data.tasks.overdue_count > 0 ? "text-red-600" : "text-zinc-900"}
                 icon={Warning} testId="kpi-overdue" />
            <Kpi label="Pending Requests" value={data.requests.pending_count}
                 accent={data.requests.pending_count > 0 ? "text-orange-700" : "text-zinc-900"}
                 icon={EnvelopeSimple} testId="kpi-requests" />
            <Kpi label="Fee Outstanding" value={money(data.projects.total_outstanding_fee)}
                 accent="text-orange-700" icon={Wallet} testId="kpi-fee-out" />
          </div>

          {/* Charts strip */}
          <ChartStrip data={data} />

          {/* Bento grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            {/* Attendance card */}
            <Card title="Today's Attendance" icon={Coffee} color="text-emerald-700" testId="glance-attendance">
              <div className="grid grid-cols-4 gap-0 border border-zinc-200 mb-3">
                <MiniStat label="Full" value={data.attendance.present_full} accent="text-emerald-700" />
                <MiniStat label="Half" value={data.attendance.present_half} accent="text-blue-700" />
                <MiniStat label="Absent" value={data.attendance.absent} accent="text-red-600" />
                <MiniStat label="Leave" value={data.attendance.on_leave} accent="text-orange-700" />
              </div>
              {data.attendance.on_leave_list.length > 0 && (
                <Chunk label="On Leave" items={data.attendance.on_leave_list.map((u) => u.name)} tone="orange" />
              )}
              {data.attendance.absent_list.length > 0 && (
                <Chunk label="Absent" items={data.attendance.absent_list.slice(0, 10).map((u) => u.name)} tone="red" />
              )}
              {(data.attendance.missed_checkins || []).filter((b) => b.count > 0).map((b) => (
                <div key={b.half} className="mt-3 border-2 border-red-600 bg-red-50 p-3" data-testid={`glance-missed-${b.half}`}>
                  <div className="label-overline text-red-700">
                    Missed {b.half === "first" ? "morning" : "evening"} check-in · {b.count} · window {b.window}
                  </div>
                  <div className="text-sm text-red-900 mt-1">{b.staff.map((u) => u.name).join(", ")}</div>
                </div>
              ))}
              <Link to="/attendance" data-testid="link-attendance"
                    className="label-overline text-emerald-700 hover:underline mt-3 inline-block">
                Open Attendance panel →
              </Link>
            </Card>

            {/* Overdue + Due 48h */}
            <Card title="Task Urgency" icon={Warning} color="text-red-600" testId="glance-tasks">
              {data.tasks.overdue_count === 0 && data.tasks.due_next_48h_count === 0 ? (
                <div className="label-overline text-emerald-700 flex items-center gap-1.5">
                  <CheckCircle size={12} weight="bold" /> All tasks on track.
                </div>
              ) : (
                <div className="space-y-3">
                  {data.tasks.overdue.length > 0 && (
                    <TaskGroup title="Overdue" tone="red" items={data.tasks.overdue} />
                  )}
                  {data.tasks.due_next_48h.length > 0 && (
                    <TaskGroup title="Due in 48h" tone="orange" items={data.tasks.due_next_48h} />
                  )}
                </div>
              )}
              <Link to="/board" data-testid="link-board" className="label-overline text-red-600 hover:underline mt-3 inline-block">
                Open Task Board →
              </Link>
            </Card>

            {/* Pending client requests */}
            <Card title="Latest Client Requests" icon={EnvelopeSimple} color="text-orange-700" testId="glance-requests">
              {data.requests.latest.length === 0 ? (
                <div className="label-overline text-zinc-500">No pending requests</div>
              ) : (
                <ul className="space-y-2">
                  {data.requests.latest.map((r) => (
                    <li key={r.id} className="text-sm border-l-2 border-orange-500 pl-2" data-testid={`req-${r.id}`}>
                      <div className="flex items-baseline gap-2">
                        <span className="font-mono text-xs text-zinc-500">{r.project}</span>
                        <span className="font-semibold">{r.client_name}</span>
                      </div>
                      <div className="text-zinc-700 truncate">{r.message}</div>
                    </li>
                  ))}
                </ul>
              )}
              <Link to="/requests" data-testid="link-requests" className="label-overline text-orange-700 hover:underline mt-3 inline-block">
                Open Client Requests →
              </Link>
            </Card>

            {/* Builder inquiries — from the public Builders Corner page */}
            <Card title="Builder Inquiries" icon={EnvelopeSimple} color="text-amber-700" testId="glance-builders">
              <div className="mb-2 flex items-baseline gap-2">
                <div className="font-display text-3xl font-black tracking-tighter text-amber-700"
                     data-testid="builder-inquiries-count">
                  {data.builder_inquiries?.new_count ?? 0}
                </div>
                <div className="label-overline text-zinc-500">new since last review</div>
              </div>
              {(data.builder_inquiries?.latest || []).length === 0 ? (
                <div className="label-overline text-zinc-500">No pending builder inquiries</div>
              ) : (
                <ul className="space-y-2">
                  {data.builder_inquiries.latest.map((b) => (
                    <li key={b.id}
                        className="text-sm border-l-2 border-amber-500 pl-2"
                        data-testid={`builder-inq-${b.id}`}>
                      <div className="flex items-baseline gap-2 flex-wrap">
                        <span className={`font-mono text-[10px] uppercase tracking-widest px-1.5 py-0.5 border ${
                          b.kind === "callback" ? "border-amber-500 text-amber-700" : "border-emerald-500 text-emerald-700"
                        }`}>
                          {b.kind === "callback" ? "Callback" : "Full inquiry"}
                        </span>
                        <span className="font-semibold">{b.contact_person || "—"}</span>
                        {b.company_name && (
                          <span className="text-xs text-zinc-500">· {b.company_name}</span>
                        )}
                      </div>
                      <div className="text-zinc-700 truncate">
                        {b.phone && <span className="font-mono text-xs mr-2">{b.phone}</span>}
                        {b.pitch}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
              <Link to="/marketing-admin?tab=inquiries" data-testid="link-builder-inquiries"
                    className="label-overline text-amber-700 hover:underline mt-3 inline-block">
                Open Builder Inquiries →
              </Link>
            </Card>

            {/* Invoices outstanding */}
            <Card title="Outstanding Invoices" icon={Receipt} color="text-orange-700" testId="glance-invoices">
              <div className="mb-2 flex items-baseline gap-2">
                <div className="font-display text-3xl font-black tracking-tighter text-orange-700">
                  {money(data.invoices.unpaid_total)}
                </div>
                <div className="label-overline text-zinc-500">{data.invoices.unpaid_count} unpaid</div>
              </div>
              {data.invoices.top_unpaid.length > 0 ? (
                <ul className="space-y-1.5">
                  {data.invoices.top_unpaid.slice(0, 5).map((i) => (
                    <li key={i.task_id} className="text-sm flex items-center gap-2" data-testid={`inv-${i.task_id}`}>
                      <span className="font-mono text-xs text-zinc-500 shrink-0">{i.invoice_number || "—"}</span>
                      <span className="truncate flex-1">{i.project} · {i.title}</span>
                      <span className="font-mono font-bold text-orange-800">{money(i.amount)}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="label-overline text-emerald-700 flex items-center gap-1.5">
                  <CheckCircle size={12} weight="bold" /> Nothing outstanding.
                </div>
              )}
              <Link to="/invoices" data-testid="link-invoices" className="label-overline text-orange-700 hover:underline mt-3 inline-block">
                Open Invoices →
              </Link>
            </Card>

            {/* Top outstanding projects */}
            <Card title="Top Outstanding Projects" icon={Buildings} color="text-blue-700" testId="glance-projects" className="lg:col-span-2">
              {data.projects.top_outstanding.length === 0 ? (
                <div className="label-overline text-zinc-500">No active projects</div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                  {data.projects.top_outstanding.map((p) => (
                    <div key={p.name} data-testid={`glance-project-${p.name}`}
                         className="border border-zinc-300 p-3 bg-zinc-50">
                      <div className="flex items-baseline gap-2">
                        <span className="label-overline text-zinc-500">Job No.</span>
                        <span className="font-display font-black text-xl tracking-tighter text-orange-600 leading-none">
                          {p.name}
                        </span>
                      </div>
                      <div className="font-display font-bold text-base text-zinc-900 truncate mt-1.5" title={p.client_name}>
                        {p.client_name || "—"}
                      </div>
                      <div className="text-[11px] text-zinc-400 truncate italic">{p.title}</div>
                      <div className="mt-2 flex items-center justify-between text-xs">
                        <span className="font-mono text-orange-700 font-bold">{money(p.outstanding)}</span>
                        <span className="font-mono text-zinc-500">{p.progress_pct}%</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <Link to="/board/projects" data-testid="link-projects" className="label-overline text-blue-700 hover:underline mt-3 inline-block">
                Open Projects →
              </Link>
            </Card>
          </div>

          <div className="text-[10px] text-zinc-400 font-mono text-right">
            Auto-refresh every 60s · Generated {new Date(data.generated_at).toLocaleTimeString()}
          </div>
        </div>
      )}
    </Layout>
  );
}

/* ---- Sub-components ---- */

function Kpi({ label, value, total, accent = "text-zinc-900", icon: Icon, testId }) {
  return (
    <div className="p-4 border-r border-b md:border-b-0 border-zinc-300 last:border-r-0" data-testid={testId}>
      <div className="flex items-center gap-1.5 label-overline text-zinc-500">
        {Icon && <Icon size={11} weight="bold" />}
        {label}
      </div>
      <div className={`font-display text-3xl font-black tracking-tighter mt-1 ${accent}`}>
        {value}{total !== undefined && <span className="text-base text-zinc-400 font-mono ml-1">/ {total}</span>}
      </div>
    </div>
  );
}

function Card({ title, icon: Icon, color, children, className = "", testId }) {
  return (
    <div className={`border-t-4 border-zinc-900 border-x border-b border-zinc-300 bg-white p-4 sm:p-5 ${className}`} data-testid={testId}>
      <div className="label-overline text-zinc-900 flex items-center gap-2 mb-3">
        {Icon && <Icon size={14} weight="bold" className={color} />}
        {title}
      </div>
      {children}
    </div>
  );
}

function MiniStat({ label, value, accent = "text-zinc-900" }) {
  return (
    <div className="p-2 border-r border-zinc-200 last:border-r-0 text-center">
      <div className="label-overline text-zinc-500">{label}</div>
      <div className={`font-display text-xl font-black tracking-tighter mt-0.5 ${accent}`}>{value}</div>
    </div>
  );
}

function Chunk({ label, items, tone = "zinc" }) {
  if (!items?.length) return null;
  const toneMap = {
    orange: "bg-orange-100 text-orange-800",
    red: "bg-red-100 text-red-700",
    zinc: "bg-zinc-200 text-zinc-700",
  };
  return (
    <div className="mb-2">
      <div className="label-overline text-zinc-500 mb-1">{label} · {items.length}</div>
      <div className="flex flex-wrap gap-1">
        {items.map((n) => (
          <span key={n} className={`label-overline px-2 py-0.5 ${toneMap[tone]}`}>{n}</span>
        ))}
      </div>
    </div>
  );
}

function TaskGroup({ title, tone, items }) {
  const border = tone === "red" ? "border-l-red-500" : "border-l-orange-500";
  return (
    <div>
      <div className="label-overline text-zinc-500 mb-1">{title} · {items.length}</div>
      <ul className="space-y-1.5">
        {items.slice(0, 5).map((t) => (
          <li key={t.id} data-testid={`urgent-${t.id}`}
              className={`text-sm border-l-4 ${border} pl-2 flex items-baseline gap-2`}>
            <span className="font-mono text-xs text-zinc-500 shrink-0">{t.project}</span>
            <span className="flex-1 truncate">
              {t.title}
              {t.is_milestone && <Flag size={10} weight="bold" className="inline ml-1 text-orange-600" />}
            </span>
            <span className="text-xs text-zinc-500 shrink-0 font-mono">{t.due_date}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}


// -----------------------------------------------------------------
// Charts strip — Attendance donut, Task-urgency bar, Fee outstanding
// -----------------------------------------------------------------
function ChartStrip({ data }) {
  const att = useMemo(() => data.attendance || {}, [data.attendance]);
  const tasks = useMemo(() => data.tasks || {}, [data.tasks]);
  const projs = useMemo(() => (data.projects && data.projects.top_outstanding) || [], [data.projects]);

  const attData = useMemo(() => ([
    { name: "Full",   value: att.present_full   || 0, fill: "#059669" },
    { name: "Half",   value: att.present_half   || 0, fill: "#2563EB" },
    { name: "Absent", value: att.absent         || 0, fill: "#DC2626" },
    { name: "Leave",  value: att.on_leave       || 0, fill: "#EA580C" },
  ]).filter((d) => d.value > 0), [att]);

  const taskData = useMemo(() => ([
    { name: "Overdue",  value: tasks.overdue_count || 0, fill: "#DC2626" },
    { name: "Due 48 h", value: tasks.due_next_48h_count || 0, fill: "#EA580C" },
  ]), [tasks]);

  const feeData = useMemo(() => (
    projs.slice(0, 6).map((p) => ({
      name: p.name || "—",
      Outstanding: Number(p.outstanding || 0),
      Collected:   Number(p.collected   || 0),
    }))
  ), [projs]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-5" data-testid="glance-charts">
      <ChartCard title="Attendance Snapshot" testId="chart-attendance">
        {attData.length === 0 ? (
          <div className="label-overline text-zinc-500 text-center py-16">No attendance data yet.</div>
        ) : (
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={attData} dataKey="value" nameKey="name" innerRadius={55} outerRadius={90} paddingAngle={2}>
                {attData.map((d, i) => <Cell key={`att-${d.name}-${i}`} fill={d.fill} />)}
              </Pie>
              <Tooltip contentStyle={{ background: "#111", border: "1px solid #333", color: "#fff", fontSize: 12 }} />
            </PieChart>
          </ResponsiveContainer>
        )}
        <div className="flex flex-wrap gap-3 justify-center mt-2">
          {attData.map((d) => (
            <div key={d.name} className="flex items-center gap-1.5 label-overline text-zinc-600">
              <span className="w-2.5 h-2.5" style={{ backgroundColor: d.fill }} /> {d.name} · {d.value}
            </div>
          ))}
        </div>
      </ChartCard>

      <ChartCard title="Task Urgency" testId="chart-tasks">
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={taskData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,.2)" />
            <XAxis dataKey="name" tick={{ fontSize: 11 }} axisLine={{ stroke: "#94A3B8" }} />
            <YAxis allowDecimals={false} tick={{ fontSize: 11 }} axisLine={{ stroke: "#94A3B8" }} />
            <Tooltip contentStyle={{ background: "#111", border: "1px solid #333", color: "#fff", fontSize: 12 }} />
            <Bar dataKey="value" radius={[2, 2, 0, 0]}>
              {taskData.map((d, i) => <Cell key={`task-${d.name}-${i}`} fill={d.fill} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>

      <ChartCard title="Fee — Outstanding vs Collected" testId="chart-fee">
        {feeData.length === 0 ? (
          <div className="label-overline text-zinc-500 text-center py-16">All projects settled.</div>
        ) : (
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={feeData} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,.2)" />
              <XAxis dataKey="name" tick={{ fontSize: 10 }} interval={0} angle={-20} textAnchor="end" height={50} />
              <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => v >= 100000 ? `${(v/100000).toFixed(1)}L` : v} />
              <Tooltip formatter={(v) => `₹${Number(v).toLocaleString("en-IN")}`}
                       contentStyle={{ background: "#111", border: "1px solid #333", color: "#fff", fontSize: 12 }} />
              <Bar dataKey="Collected"   stackId="a" fill="#059669" radius={[0, 0, 0, 0]} />
              <Bar dataKey="Outstanding" stackId="a" fill="#EA580C" radius={[2, 2, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </ChartCard>
    </div>
  );
}

function ChartCard({ title, testId, children }) {
  return (
    <div data-testid={testId} className="border border-zinc-300 bg-white p-4">
      <div className="label-overline text-zinc-600 mb-2">{title}</div>
      {children}
    </div>
  );
}

